// services/admin/locationService.js
import { apiClient } from './apiClient';

export const locationService = {
  /**
   * Создание локации
   * @param {Object} locationData - Данные локации
   * @returns {Promise<Location>}
   */
  async createLocation(locationData) {
    const response = await apiClient.post('/locations', locationData);
    return response; 
  },

  /**
   * Получение основной фотографии локации
   * @param {number} locationId - ID локации
   * @returns {Promise<Photo>}
   */
  async getMainPhoto(locationId) {
    return apiClient.get(`/locations/${locationId}/main-photo`);
  },

  /**
   * Загрузка основного фото для локации
   * @param {number} locationId - ID локации
   * @param {File} photo - Файл фотографии
   * @returns {Promise<Photo>}
   */
  async uploadMainPhoto(locationId, photo) {
    const formData = new FormData();
    formData.append('photo', photo);
    
    // Используем postFormData для загрузки файлов
    return apiClient.postFormData(`/locations/${locationId}/main-photo`, formData);
  },

  /**
   * Получение всех фото локации
   * @param {number} locationId - ID локации
   * @returns {Promise<Photo[]>}
   */
  async getLocationPhotos(locationId) {
    const response = await apiClient.get(`/locations/${locationId}/photos`);
    return response; 
  },
  
  /**
   * Загрузка фото для локации
   * @param {number} locationId - ID локации
   * @param {File[]} photos - Массив файлов фотографий
   * @returns {Promise<Photo[]>}
   */
  async uploadLocationPhotos(locationId, photos) {
    const formData = new FormData();
    photos.forEach(photo => {
      formData.append('photos', photo);
    });

    return apiClient.postFormData(`/locations/${locationId}/photos`, formData);
  },

  /**
   * Удаление фото локации
   * @param {number} locationId - ID локации
   * @param {number} photoId - ID фото
   * @returns {Promise<void>}
   */
  async deleteLocationPhoto(locationId, photoId) {
    await apiClient.delete(`/locations/${locationId}/photos/${photoId}`);
    // Ничего не возвращаем
  },

  /**
   * Получение всех фото точки
   * @param {number} pointId - ID точки
   * @returns {Promise<Photo[]>}
   */
  async getPointPhotos(pointId) {
    const response = await apiClient.get(`/points/${pointId}/photos`);
    return response; 
  },
  
  /**
   * Загрузка фото для точки
   * @param {number} pointId - ID точки
   * @param {File[]} photos - Массив файлов фотографий
   * @returns {Promise<Photo[]>}
   */
  async uploadPointPhotos(pointId, photos) {
    const formData = new FormData();
    photos.forEach(photo => {
      formData.append('photos', photo);
    });

    return apiClient.postFormData(`/points/${pointId}/photos`, formData);
  },

  /**
   * Получение всех локаций
   * @returns {Promise<Location[]>}
   */
  async getAllLocations() {
    const response = await apiClient.get('/locations');
    return response; 
  },

  /**
   * Получение локации по ID
   * @param {number} id - ID локации
   * @returns {Promise<Location>}
   */
  async getLocationById(id) {
    const response = await apiClient.get(`/locations/${id}`);
    return response; 
  },

  /**
   * Обновление локации
   * @param {number} id - ID локации
   * @param {Object} locationData - Данные для обновления
   * @returns {Promise<Location>}
   */
  async updateLocation(id, locationData) {
    const response = await apiClient.put(`/locations/${id}`, locationData);
    return response; 
  },

  /**
   * Частичное обновление локации (PATCH)
   * @param {number} id - ID локации
   * @param {Object} locationData - Данные для обновления
   * @returns {Promise<Location>}
   */
  async patchLocation(id, locationData) {
    const response = await apiClient.patch(`/locations/${id}`, locationData);
    return response; 
  },

  /**
   * Удаление локации
   * @param {number} id - ID локации
   * @returns {Promise<void>}
   */
  async deleteLocation(id) {
    console.log(`DELETE запрос к: /locations/${id}`);
    try {
      const response = await apiClient.delete(`/locations/${id}`);
      console.log('Ответ сервера при удалении:', response);
      return response;
    } catch (error) {
      console.error('Ошибка в deleteLocation:', error);
      throw error;
    }
  },

  /**
   * Получение точек локации
   * @param {number} locationId - ID локации
   * @returns {Promise<Point[]>}
   */
  async getLocationPoints(locationId) {
    const response = await apiClient.get(`/locations/${locationId}/points`);
    return response; 
  },

  /**
   * Получение дополнительной информации локации
   * @param {number} locationId - ID локации
   * @returns {Promise<AdditionalInfo[]>}
   */
  async getLocationAdditionalInfo(locationId) {
    const response = await apiClient.get(`/locations/${locationId}/additional-info`);
    return response; 
  },

  /**
   * Получение локаций по ID категории
   * @param {number} categoryId - ID категории
   * @returns {Promise<Location[]>}
   */
  async getLocationsByCategoryId(categoryId) {
    const response = await apiClient.get(`/categories/${categoryId}/locations`);
    return response; 
  },

  /**
   * Получение всех категорий локации
   * @param {number} locationId - ID локации
   * @returns {Promise<Category[]>}
   */
  async getLocationCategories(locationId) {
    const response = await apiClient.get(`/locations/${locationId}/categories`);
    return response; 
  },
  
  /**
   * Получение локаций по ID тега
   * @param {number} tagId - ID тега
   * @returns {Promise<Location[]>}
   */
  async getLocationsByTagId(tagId) {
    const response = await apiClient.get(`/tags/${tagId}/locations`);
    return response; 
  },

  /**
   * Получение всех тегов локации
   * @param {number} locationId - ID локации
   * @returns {Promise<Tag[]>}
   */
  async getLocationTags(locationId) {
    const response = await apiClient.get(`/locations/${locationId}/tags`);
    return response; 
  },

  /**
   * Поиск локаций
   * @param {Object} params - Параметры поиска
   * @returns {Promise<Location[]>}
   */
  async searchLocations(params = {}) {
    const queryParams = new URLSearchParams();
    if (params.name) queryParams.append('name', params.name);
    if (params.categoryId) queryParams.append('categoryId', params.categoryId);
    if (params.tagId) queryParams.append('tagId', params.tagId);
    if (params.city) queryParams.append('city', params.city);
    
    const queryString = queryParams.toString();
    const endpoint = queryString ? `/locations/search?${queryString}` : '/locations';
    
    const response = await apiClient.get(endpoint);
    return response; 
  },

  /**
   * Обновление категорий локации
   * @param {number} locationId - ID локации
   * @param {number[]} categoryIds - Массив ID категорий
   * @returns {Promise<Location>}
   */
  async updateLocationCategories(locationId, categoryIds) {
    const response = await apiClient.put(`/locations/${locationId}/categories`, { categoryIds });
    return response;
  },

  /**
   * Обновление тегов локации
   * @param {number} locationId - ID локации
   * @param {number[]} tagIds - Массив ID тегов
   * @returns {Promise<Location>}
   */
  async updateLocationTags(locationId, tagIds) {
    const response = await apiClient.put(`/locations/${locationId}/tags`, { tagIds });
    return response;
  }
};